import api, { mediaUrl } from './api';
import {
  AllContent, ContentMap, PublicMedia, MediaAsset, LegalDoc, LegalKey,
  ContactPayload, ContactMessage, Lang, ContentBlockDto,
  PublicProduct, PublicService, PublicCertification, PublicLocation, PublicFaq,
  CollectionKey, ReorderItem,
} from '../types';

/** Normalize a PublicMedia payload so every url is absolute. */
function absolutizeMedia(m: PublicMedia): PublicMedia {
  return {
    single: Object.fromEntries(Object.entries(m.single || {}).map(([k, v]) => [k, mediaUrl(v)])),
    galleries: Object.fromEntries(Object.entries(m.galleries || {}).map(([k, arr]) => [k, (arr || []).map(mediaUrl)])),
    customers: (m.customers || []).map(c => ({ name: c.name, url: mediaUrl(c.url) })),
  };
}

/** Guards against a proxy/CDN returning HTML (or any non-JSON) for an /api call in
 *  production: only treat true arrays/objects as data, otherwise fall back to empty
 *  so the page can never crash on `.map` of a string. */
function arr<T>(data: unknown): T[] { return Array.isArray(data) ? (data as T[]) : []; }
function isObj(data: unknown): boolean { return !!data && typeof data === 'object' && !Array.isArray(data); }
export const PublicApi = {
  async content(lang: Lang): Promise<ContentMap> {
    const { data } = await api.get(`/public/content`, { params: { lang } });
    return isObj(data) ? data : {};
  },
  async allContent(): Promise<AllContent> {
    const { data } = await api.get(`/public/content/all`);
    return (isObj(data) ? data : null) as AllContent;
  },
  async media(): Promise<PublicMedia> {
    const { data } = await api.get(`/public/media`);
    return absolutizeMedia((isObj(data) ? data : { single: {}, galleries: {}, customers: [] }) as PublicMedia);
  },
  async legal(docKey: LegalKey, lang: Lang): Promise<LegalDoc> {
    const { data } = await api.get(`/public/legal/${docKey}`, { params: { lang } });
    return (isObj(data) ? data : null) as LegalDoc;
  },
  async contact(payload: ContactPayload): Promise<any> {
    const { data } = await api.post(`/public/contact`, payload);
    return data;
  },  async products(lang: Lang): Promise<PublicProduct[]> {
    const { data } = await api.get(`/public/products`, { params: { lang } });
    return arr<PublicProduct>(data).map((p) => ({
      ...p,
      previewUrl: p.previewUrl ? mediaUrl(p.previewUrl) : null,
      gallery: (p.gallery || []).map(mediaUrl),
    }));
  },
  async services(lang: Lang): Promise<PublicService[]> {
    const { data } = await api.get(`/public/services`, { params: { lang } });
    return arr<PublicService>(data).map((s) => ({ ...s, gallery: (s.gallery || []).map(mediaUrl) }));
  },
  async certifications(lang: Lang): Promise<PublicCertification[]> {
    const { data } = await api.get(`/public/certifications`, { params: { lang } });
    return arr<PublicCertification>(data);
  },
  async locations(lang: Lang): Promise<PublicLocation[]> {
    const { data } = await api.get(`/public/locations`, { params: { lang } });
    return arr<PublicLocation>(data);
  },
  async faq(lang: Lang): Promise<PublicFaq[]> {
    const { data } = await api.get(`/public/faq`, { params: { lang } });
    return arr<PublicFaq>(data);
  },
};
export const AuthApi = {
  async login(email: string, password: string): Promise<string> {
    // Backend AuthController maps login to /api/auth/public/login and wraps the
    // AuthResponse in Response<T> (data.token). Fall back to /auth/login if the
    // package rename lands on the CONTRACT path.
    let data: any;
    try {
      ({ data } = await api.post(`/auth/public/login`, { email, password }));
    } catch (err: any) {
      if (err?.response && err.response.status === 404) {
        ({ data } = await api.post(`/auth/login`, { email, password }));
      } else {
        throw err;
      }
    }
    // Try common token field names + renewed-token header handled by interceptor.
    const token = data?.data?.token || data?.token || data?.data?.jwt || data?.jwt || data?.accessToken;
    if (token) localStorage.setItem('authToken', token);
    return token;
  },
  logout() { localStorage.removeItem('authToken'); },
  isAuthenticated(): boolean { return !!localStorage.getItem('authToken'); },
};
export const AdminApi = {
  async allContent(): Promise<AllContent> {
    const { data } = await api.get(`/admin/content/all`);
    return data?.data ?? data;
  },
  async saveContent(blocks: ContentBlockDto[]): Promise<any> {
    const { data } = await api.put(`/admin/content`, blocks);
    return data;
  },
  async media(): Promise<MediaAsset[]> {
    const { data } = await api.get(`/admin/media`);
    const list: MediaAsset[] = data?.data ?? data;
    return (list || []).map(a => ({ ...a, url: mediaUrl(a.url || `/api/public/media/file/${a.filename}`) }));
  },
  async uploadMedia(form: FormData): Promise<any> {
    const { data } = await api.post(`/admin/media`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  async updateMedia(id: number, payload: { label?: string; alt?: string; sortOrder?: number }): Promise<any> {
    const { data } = await api.put(`/admin/media/${id}`, payload);
    return data;
  },
  async reorderMedia(order: { id: number; sortOrder: number }[]): Promise<any> {
    const { data } = await api.put(`/admin/media/reorder`, order);
    return data;
  },
  async deleteMedia(id: number): Promise<any> {
    const { data } = await api.delete(`/admin/media/${id}`);
    return data;
  },
  async legal(docKey: LegalKey, lang: Lang): Promise<LegalDoc> {
    const { data } = await api.get(`/admin/legal/${docKey}/${lang}`);
    return data?.data ?? data;
  },
  async saveLegal(docKey: LegalKey, lang: Lang, doc: LegalDoc): Promise<any> {
    const { data } = await api.put(`/admin/legal/${docKey}/${lang}`, doc);
    return data;
  },
  async messages(): Promise<ContactMessage[]> {
    const { data } = await api.get(`/admin/contact`);
    return data?.data ?? data;
  },
};
export const CollectionApi = {
  async list<T>(coll: CollectionKey): Promise<T[]> {
    const { data } = await api.get(`/admin/${coll}`);
    return (data?.data ?? data) as T[];
  },
  async create<T>(coll: CollectionKey, entity: T): Promise<T> {
    const { data } = await api.post(`/admin/${coll}`, entity);
    return (data?.data ?? data) as T;
  },
  async update<T extends { id?: number | null }>(coll: CollectionKey, entity: T): Promise<T> {
    const { data } = await api.put(`/admin/${coll}/${entity.id}`, entity);
    return (data?.data ?? data) as T;
  },
  async remove(coll: CollectionKey, id: number): Promise<any> {
    const { data } = await api.delete(`/admin/${coll}/${id}`);
    return data;
  },
  async reorder(coll: CollectionKey, order: ReorderItem[]): Promise<any> {
    const { data } = await api.put(`/admin/${coll}/reorder`, order);
    return data;
  },
};
